import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { THEME } from '../config/copy';
function computeBand(series) {
    const closes = series.map((point) => point.close);
    const returns = closes.slice(1).map((close, index) => close / closes[index] - 1);
    const mean = returns.reduce((sum, value) => sum + value, 0) / Math.max(returns.length, 1);
    const variance = returns.reduce((sum, value) => sum + (value - mean) ** 2, 0) / Math.max(returns.length - 1, 1);
    const deviation = Math.sqrt(variance);
    const start = closes.at(-1) ?? 0;
    const median = [];
    const lower = [];
    const upper = [];
    for (let day = 1; day <= 20; day += 1) {
        const expected = start * Math.exp(mean * day);
        const spread = start * deviation * Math.sqrt(day) * 1.645;
        median.push(expected);
        lower.push(Math.max(0, expected - spread));
        upper.push(expected + spread);
    }
    return { median, lower, upper };
}
export function FanChart({ series }) {
    const elementRef = useRef(null);
    useEffect(() => {
        if (!elementRef.current || series.length < 20)
            return;
        const chart = echarts.init(elementRef.current, undefined, { renderer: 'canvas' });
        const band = computeBand(series);
        const xAxis = Array.from({ length: 20 }, (_, index) => `Day ${index + 1}`);
        const spread = band.upper.map((value, index) => value - band.lower[index]);
        chart.setOption({
            animationDuration: 350,
            grid: { left: 52, right: 20, top: 34, bottom: 34 },
            tooltip: { trigger: 'axis', valueFormatter: (value) => `$${value.toFixed(2)}` },
            legend: { data: ['Median Scenario', '90% Historical Confidence Band'], textStyle: { color: '#60726B' } },
            xAxis: { type: 'category', data: xAxis, axisLine: { lineStyle: { color: '#D7E0DB' } } },
            yAxis: { type: 'value', axisLabel: { color: '#60726B', formatter: (value) => `$${value.toFixed(0)}` }, splitLine: { lineStyle: { color: '#E6ECE9' } } },
            series: [
                { name: 'Lower Bound', type: 'line', stack: 'band', data: band.lower, symbol: 'none', lineStyle: { opacity: 0 }, areaStyle: { opacity: 0 } },
                { name: '90% Historical Confidence Band', type: 'line', stack: 'band', data: spread, symbol: 'none', lineStyle: { opacity: 0 }, areaStyle: { color: 'rgba(36, 91, 78, 0.18)' } },
                { name: 'Median Scenario', type: 'line', data: band.median, symbol: 'none', smooth: true, lineStyle: { color: THEME.forest, width: 2.5 } }
            ]
        });
        const observer = new ResizeObserver(() => chart.resize());
        observer.observe(elementRef.current);
        return () => {
            observer.disconnect();
            chart.dispose();
        };
    }, [series]);
    return _jsx("div", { ref: elementRef, className: "h-72 w-full", "aria-label": "20-Day Projected Volatility Range Chart" });
}
